import 'vite/modulepreload-polyfill'

import { createApp } from 'vue'
import router from './router'
import { createPinia } from 'pinia'

import Aura from '@primevue/themes/aura'
import PrimeVue from 'primevue/config'
import ConfirmationService from 'primevue/confirmationservice'
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip'
import KeyFilter from 'primevue/keyfilter'
import VueDOMPurifyHTML from 'vue-dompurify-html'
import VueKonva from 'vue-konva'

import App from './App.vue'

import '@/assets/styles.scss'
import '@/assets/tailwind.css'
import { definePreset } from '@primevue/themes'
import { Api } from './api'
import { fmsApi } from '@messe-esang/fms-api-js-wrapper'
import { useMemberStore } from './stores/member'

import { generatePalette, getPrimary, primaries } from '@/composables/theme'
import { useSiteStore } from './stores/site'
import { format } from './modules/formatter'
import i18n from './i18n'

const pinia = createPinia()
const app = createApp(App)

import ky from 'ky'
import { useExhibitorStore } from './stores/exhibitor'
import { useFairStore } from './stores/fair'
const api = ky.extend({
  hooks: {
    beforeRequest: [
      () => {
        common.showProgress()
      }
    ],
    beforeError: [
      (error) => {
        common.hideProgress()
        return error
      }
    ],
    afterResponse: [
      async (request, options, response) => {
        common.hideProgress()
        if (response.status === 401) {
          const memberStore = useMemberStore()
          if (!memberStore.loginMember.isEmpty()) {
            common.toast({ detail: i18n.global.t('alert.expiredSession'), severity: 'warn', life: 10000 })
            setTimeout(() => {
              location.reload()
            }, 1000)
          }
        }
        return response
      }
    ]
  },
  timeout: 60000
})

const exhibitorApi = new Api({ baseUrl: import.meta.env.VITE_APP_API_URL, customFetch: api })

app.provide('fmsApi', fmsApi)
app.provide('exhibitorApi', exhibitorApi)

app.config.globalProperties.common = globalThis.common
app.config.globalProperties.format = format
app.config.globalProperties.isProduction = import.meta.env.PROD
app.config.globalProperties.isDevelopment = import.meta.env.DEV
app.config.globalProperties.isKoreanLocale = () => {
  return globalThis.common?.locale.value === 'ko'
}
app.config.globalProperties.isEnglishLocale = () => {
  return globalThis.common?.locale.value === 'en'
}

if (globalThis.initOptions.customThemes) {
  globalThis.initOptions.customThemes.forEach((theme) => primaries.push(theme))
}

globalThis.initOptions.themeConfigs.forEach((config) => {
  if (primaries.find((p) => p.name === config.theme)) {
    return
  }

  if (!config.theme || !config.primaryColor) {
    return
  }
  primaries.push(generatePalette(config.theme, config.primaryColor))
})

const primary = getPrimary(globalThis.initOptions.siteConfig.fairMasterCode ?? 'default')

const preset = definePreset(Aura, {
  semantic: {
    primary: primary?.palette
  }
})

router.beforeEach(async (to) => {
  const memberStore = useMemberStore()
  const siteStore = useSiteStore()

  if (to.meta.requireAuth) {
    const unauthorized = memberStore.loginMember.isEmpty()
    const redirect = siteStore.siteConfig.basePath + to.fullPath

    if (unauthorized) {
      return { name: 'login', query: { redirect } }
    }
  }

  if (!memberStore.loginMember.isEmpty()) {
    await siteStore.initialize()
  }

  if (to.meta.isRequireExhibitorId) {
    const exhibitorStore = useExhibitorStore()
    const fairStore = useFairStore()
    const fairId = fairStore.fair?.id ?? fairStore.lastFairId

    if (!fairId) {
      return { name: 'root-fair_list' }
    }

    if (!exhibitorStore.exhibitor.exhibitorId) {
      common.toast({ detail: i18n.global.t('alert.exhibitorIdRequired'), severity: 'warn', life: 10000 })
      return { name: 'exhibition-application-submit', params: { FAIR_ID: fairStore.fair?.id ?? fairStore.lastFairId } }
    } else if (exhibitorStore.exhibitor.exhibitorId && to.meta.isRequireApproval && exhibitorStore.exhibitor.status !== 'CompleteApproval') {
      common.toast({ detail: i18n.global.t('alert.waitingApproval'), severity: 'warn', life: 10000 })
      return { name: 'exhibition-application-check', params: { FAIR_ID: fairStore.fair?.id ?? fairStore.lastFairId } }
    }
  }
})

app.directive('tooltip', Tooltip)
app.directive('keyfilter', KeyFilter)

app.use(pinia)
app.use(router)
app.use(i18n)
app.use(VueDOMPurifyHTML, {
  default: {
    ADD_ATTR: ['target']
  }
}) // v-html directive with DOMPurify : XSS 방지 v-dompurify-html="content"

app.use(PrimeVue, {
  theme: {
    preset: preset,
    options: {
      darkModeSelector: '.app-dark'
    }
  }
})
app.use(ToastService)
app.use(ConfirmationService)
app.use(VueKonva, { prefix: 'V' })

app.mount('#app')
